import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { BaseAssetSettings } from '../../asset/BaseAsset';
import { assertArray, assertNumberBetween } from '../../../validators';
import { getAttributesToSerialize, NumberAttribute, StringAttribute } from '../../attributes';
import { unicodeToChar, notifyBugsnag, distance } from '../../../libs/other';
import type { CanvasTextRendererSettings } from '../renderer/CanvasTextRenderer';
import { CanvasTextRenderer } from '../renderer/CanvasTextRenderer';
import { BaseTextCanvasAsset } from '../../canvasRenderer/asset/BaseTextCanvasAsset';
import { CanvasTextStyleRenderer } from '../renderer/CanvasTextStyle';
import { SeekMode, DynamicLevel } from '../../../enums';
import type { DestroyOptions, Coordinate } from '../../../types';
import type { CanvasTextDynamicGraphicRendererSettings } from '../renderer/CanvasTextDynamicGraphic';
import { CanvasFontSizeToFitAllTextBinarySearch } from '../metrics/CanvasFontSizeToFitAllTextBinarySearch';
import { buildMetricsForText } from '../metrics/CanvasTextMetrics';
import { Config } from '../../../video-data/config';
import { CanvasTextAssetStyle } from './CanvasTextAssetStyle';
import { CanvasTextEditingManager } from './CanvasTextEditingManager';

export type TextEditingOptions = {
    minSize: number;
};

/**
 * A set of properties that exist on the normal and highlight styles that can be
 * edited by the user via controls exposed in the video creator
 */
type TextEditingStyles = {
    /**
     * A localized value relative to the video's aspect ratio and other configuration
     */
    fontSize?: number;
};

const DefaultTextEditingOptions = {
    minSize: 40,
};


/**
 * CanvasTextAsset is the Luminary equivalent of CanvasText and provides a bridge between Luminary and the CanvasText
 * library. It does this mirroring the CanvasText API which hierarchy looks like:
 * text
 * +- normal style
 *   +- dynamic styling (color, mask, object)
 * +- highlight style
 *  +- dynamic styling (color, mask, object)
 */
export class CanvasTextAsset extends BaseTextCanvasAsset<CanvasTextRenderer> {
    override _classType = 'CanvasTextAsset';

    @StringAttribute()
    text = '';
    /**
     * Highlight text scale multiplier based on the normal style font size. This is used to automatically change the
     * font size of the highlighted text when changing the normal text font size which happens when the user changes
     * the font size manually.
     **/
    @NumberAttribute()
    highlightedTextScale = 1.0;

    #sanitizedText = '';
    #highlightAreas: [number, number][] = [];

    #styles: {
        normalStyle: CanvasTextAssetStyle;
        highlightStyle: CanvasTextAssetStyle;
    };

    #textEditingManager: CanvasTextEditingManager = null;

    /**
     * @internal
     */
    _textEditingOptions: TextEditingOptions = { ...DefaultTextEditingOptions };

    constructor(container, settings: BaseAssetSettings) {
        const renderer = new CanvasTextRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            redrawParentOnUpdate: true,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        };
        const normalStyle = new CanvasTextAssetStyle(styleSettings);
        const highlightStyle = new CanvasTextAssetStyle(styleSettings);

        // By default, render highlights as part of the first render pass
        normalStyle.renderHighlighted = true;
        normalStyle.renderHighlightedColor = true;
        highlightStyle.renderHighlighted = false;
        // This doesn't actually have any effect. Once CanvasTextAsset and TextAsset are consolidated, we should
        // consider removing renderHighlightedColor from the CanvasTextAssetStyle and make it a CanvasTextAsset property
        highlightStyle.renderHighlightedColor = true;

        this.#styles = { normalStyle, highlightStyle };
    }

    get _styles() {
        return {
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
        };
    }

    get textHighlights(): [number, number][] {
        return this.#highlightAreas;
    }

    set textHighlights(value: [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#highlightAreas, value)) {
            this.#highlightAreas = value;
            this.requiresRedraw = true;
        }
    }

    get _wordElements() {
        return this.renderer.metrics.lines.map(line => line.words.map(word => word.elements));
    }

    get textLinesForEditing() {
        return this.renderer.metrics.lines;
    }

    get #stylesList(): CanvasTextAssetStyle[] {
        return Object.values(this.#styles);
    }

    get normalStyle(): CanvasTextAssetStyle {
        return this.#styles.normalStyle;
    }

    get highlightStyle(): CanvasTextAssetStyle {
        return this.#styles.highlightStyle;
    }

    override _checkAndFixProperties() {
        this.#sanitizedText = unicodeToChar(this.text);
        this.#highlightAreas = sanitizeHighlights(this.#sanitizedText, this.#highlightAreas);
    }

    getFontSizeToFitAllText(fontSizeRange = { min: 50, max: 250 }, text = this.text) {
        const canvasTextSettings = this.getCanvasRendererSettings();
        // TODO: we create a CanvasTextStyle here for getFontSizeToFitAllText which is confusing. We should refactor this.
        const normalStyle = new CanvasTextStyleRenderer().apply(canvasTextSettings.normalStyle);
        const highlightStyle = new CanvasTextStyleRenderer().apply(canvasTextSettings.highlightStyle);

        const canvasFontSizeRange = {
            min: Math.floor(fontSizeRange.min * this.referenceToOutputMultiplier),
            max: Math.floor(fontSizeRange.max * this.referenceToOutputMultiplier),
        };

        const { fontSize: canvasFontSize, displaysAllText } = CanvasFontSizeToFitAllTextBinarySearch.search({
            text,
            alignment: canvasTextSettings.alignment,
            normalStyle,
            highlightStyle,
            highlightAreas: canvasTextSettings.highlightAreas,
            wordWrap: canvasTextSettings.wordWrap,
            wordWrapWidth: canvasTextSettings.maxTextWidth,
            maxHeight: canvasTextSettings.maxTextHeight,
            fontSizeRange: canvasFontSizeRange,
        });
        const assetFontSize = Math.floor(canvasFontSize / this.referenceToOutputMultiplier);

        return {
            fontSize: assetFontSize,
            baseFontSize: Math.floor(assetFontSize / this.textScaleFactor),
            displaysAllText,
        };
    }

    get hasText() {
        return this.text.trim().length !== 0;
    }

    override load(dataObj: any): this {
        super.load(dataObj);
        if (dataObj.classType === Config.TEXT_ASSET_TYPE) {
            this.applyTextAssetStyle(dataObj);
        }
        return this;
    }

    /**
     * applyTextAssetStyle allows us to load the style settings from a text asset onto our canvas text asset.
     */
    applyTextAssetStyle(settings) {
        // here we use a batchRedraw to avoid unnecessary rebuilds
        this._batchRedraw(() => {
            if (settings.textSize !== undefined) {
                this.textSize = settings.textSize;
            }
            if (settings.textAlign !== undefined) {
                this.textAlign = settings.textAlign;
            }
            if (settings.leadingMultiplier !== undefined) {
                this.leadingMultiplier = settings.leadingMultiplier;
            }
            if (settings.textScaleFactor !== undefined) {
                this.textScaleFactor = settings.textScaleFactor;
            }
            if (settings.maxHeight !== undefined) {
                this.maxHeight = settings.maxHeight;
            }
            if (settings.maxWidth !== undefined) {
                this.maxWidth = settings.maxWidth;
            }

            if (settings.transformation !== undefined) {
                this.transformation.load(settings.transformation);
            }

            const scaledFontSize = this.textSize * this.textScaleFactor;
            const fontSize = scaledFontSize;
            const leading = scaledFontSize * this.leadingMultiplier;

            this.normalStyle.loadTextAssetStyle({
                ...settings.normalStyle,
                fontSize,
                leading,
            });

            this.highlightStyle.loadTextAssetStyle({
                ...settings.highlightStyle,
                fontSize,
                leading,
            });
        });
    }

    override getCanvasRendererSettings(): CanvasTextRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        const renderSettings = {
            ...super.getCanvasRendererSettings(),
            text: this.#sanitizedText,
            alignment: this.textAlign,
            highlightAreas: this.#highlightAreas,
            wordWrap: this.wordWrap,
            normalStyle: this.normalStyle.getCanvasRendererSettings(),
            highlightStyle: this.highlightStyle.getCanvasRendererSettings(),
        };
        this.#manipulateRenderSettings(renderSettings);
        return renderSettings;
    }

    /**
     * @internal
     */
    get _textEditingManager(): CanvasTextEditingManager {
        return this.#textEditingManager;
    }

    /**
     * Whether or not text editing mode is enabled
     */
    get hasTextEditingEnabled(): boolean {
        return this.#textEditingManager !== null;
    }

    /**
     * Returns information used to force the window selection to
     * a default state when a user starts to edit text. If no coordinate is provided,
     * then this method assumes the cursor position will be at the very beginning
     *
     * @param x x-axis click coord, relative to luminary's resolution
     * @param y y-axis click coord, relative to luminary's resolution
     */
    getEditingCursorPosition(x = 0, y = 0): { node: Node; offset: number; } {
        if (!this.hasTextEditingEnabled) {
            return null;
        }
        assertNumberBetween(x, 0, this.root.referenceResolution.width);
        assertNumberBetween(y, 0, this.root.referenceResolution.height);

        return this.#textEditingManager.calculateCursorPos(x, y);
    }

    /**
     * Creates HTML nodes used for user interaction to update this text asset's text property. The element
     * gets its default styling, positioning, and event handlers configured here. For each change, the element's
     * children are rebuilt and repositioned to ensure alignment.
     */
    enableTextInput(domContainer: HTMLElement, options?: TextEditingOptions) {
        if (this.hasTextEditingEnabled) {
            return;
        }
        try {
            this.#textEditingManager = new CanvasTextEditingManager(this, domContainer);
            this.requiresRedraw = true;
            if (options) {
                this._textEditingOptions = options;
            }
        }
        catch (e) {
            // if something fails, make sure the texteditingmanager is destroyed
            if (this.#textEditingManager) {
                this.#textEditingManager.destroy();
            }
            this.#textEditingManager = null;
            throw (e);
        }
    }

    /**
     * Disables text editing by removing the input element from the DOM and destroying its reference.
     */
    disableTextInput() {
        if (!this.hasTextEditingEnabled) {
            return;
        }
        this.#textEditingManager.destroy();
        this.#textEditingManager = null;

        // do not allow a final text value comprised only of whitespaces/newlines
        if (this.text.trim().length === 0) {
            this.text = '';
        }
        else {
            this.requiresRedraw = true;
        }
    }

    /**
     * If the user is editing text, whether or not the last change they made was too large
     * for this text asset to fully display
     */
    get inOverflowState(): boolean {
        return this.hasTextEditingEnabled ?
            this.#textEditingManager.inOverflowState : false;
    }

    /**
     * Updates textSize if asset can fit text with the new text size, otherwise, sets inOverflowState to true.
     */
    tryToUpdateTextSize(value: number) {
        if (this.textSize === value) {
            return;
        }
        if (!this.hasTextEditingEnabled) {
            throw Error('Cannot call `tryToUpdateTextSize` outside of textEditing mode. Use `textSize` setter instead');
        }
        const canChangeFit = this.canFitText(this.text, {
            fontSize: value * this.textScaleFactor,
        });

        if (!canChangeFit) {
            this.#textEditingManager.inOverflowState = true;
            return;
        }
        else if (this.#textEditingManager.inOverflowState) {
            this.#textEditingManager.inOverflowState = false;
        }

        this.textSize = value;
    }

    /**
     * Fires a custom web event notifying listeners that an unrecoverable error has occurred while
     * editing, logs the error, and exits edit mode
     */
    #handleEditingException(e: Error) {
        if (this.hasTextEditingEnabled) {
            const webEvent = new Event(CanvasTextEditingManager.NTE_EVENT_EDITING_EXCEPTION);
            this.#textEditingManager.domContainer.dispatchEvent(webEvent);
            this.disableTextInput();
        }
        notifyBugsnag(e, 'error', {});
        console.error(e);
    }

    getBoundsOfTextWithoutGraphics() {
        const bounds = this.getBounds().clone();
        bounds.x -= this.renderer.totalBounds.x / this.referenceToOutputMultiplier;
        bounds.y -= this.renderer.totalBounds.y / this.referenceToOutputMultiplier;
        bounds.width = (this.renderer.metrics.bounds.width) / this.referenceToOutputMultiplier;
        bounds.height = (this.renderer.metrics.bounds.height) / this.referenceToOutputMultiplier;

        return bounds;
    }

    /**
     * Determines if an arbitrary text value and optionally a set of different styles
     * can be fully displayed or not
     */
    canFitText(newText: string = '', newStyles: TextEditingStyles = {}): boolean {
        const canvasTextSettings = this.getCanvasRendererSettings();
        // TODO: we create a CanvasTextStyle here for getFontSizeToFitAllText which is confusing. We should refactor this.
        const normalStyle = new CanvasTextStyleRenderer().apply(canvasTextSettings.normalStyle);
        const highlightStyle = new CanvasTextStyleRenderer().apply(canvasTextSettings.highlightStyle);

        if (newStyles.fontSize) {
            normalStyle.fontSize = newStyles.fontSize;
            highlightStyle.fontSize = newStyles.fontSize * this.highlightedTextScale;
        }

        const textMetrics = buildMetricsForText({
            text: newText,
            alignment: canvasTextSettings.alignment,
            normalStyle,
            highlightStyle,
            highlightAreas: canvasTextSettings.highlightAreas,
            wordWrap: canvasTextSettings.wordWrap,
            wordWrapWidth: canvasTextSettings.maxTextWidth,
            maxHeight: canvasTextSettings.maxTextHeight,
        });

        return textMetrics.canDisplayAllText;
    }

    /**
     * Invokes the build process of the text editing manager. This method should be included
     * in any build loop so that the text editing input stays up to date.
     */
    updateTextInput() {
        if (!this.hasTextEditingEnabled) {
            return;
        }
        try {
            // Here, animations have not been applied yet.
            // We call _seek() so that PIXI lines and words match
            // NTE text bounds, according to any animation that could be applied.
            // Animations can impact text position in some scene designs.
            // An example is when a constant offset is applied via an animation.
            const timeParent = this.subscene ?? this.slide;
            this._seek(timeParent._timeline.progress, SeekMode.ResolveWhenSingleFrame);
            this.#textEditingManager._build();
        }
        catch (e) {
            this.#textEditingManager.resetCalculatedState();
            this.#handleEditingException(e);
        }
    }

    override destroy(options?: DestroyOptions): void {
        this.#textEditingManager?.destroy();
        super.destroy(options);
    }

    /**
     * This manipulates the render settings before passing them to the renderer. This is a helpful step to ensure that
     * we maintain similar behaviour to the old TextAsset and its animation handling.
     * Once we've migrated to CanvasTextAsset then we can modify the video data with another migration in lieu of
     * this method, see LU-3289.
     */
    #manipulateRenderSettings(settings: CanvasTextRendererSettings) {
        const normalStyle = settings.normalStyle;
        const highlightStyle = settings.highlightStyle;

        // highlight level animations on the normal style would actually take effect on the highlight style,
        // so we move those animation configurations to the highlight style
        normalStyle.animationConfigurations.forEach(animationConfig => {
            if (animationConfig.level === DynamicLevel.highlight) {
                highlightStyle.animationConfigurations.push(animationConfig);
            }
        });
        normalStyle.animationConfigurations = normalStyle.animationConfigurations.filter(animationConfig => {
            return animationConfig.level !== DynamicLevel.highlight;
        });

        // object level position animations on the highlight style have no effect, so filter those out
        highlightStyle.animationConfigurations = highlightStyle.animationConfigurations.filter(animationConfig => {
            if (animationConfig.property === 'x' || animationConfig.property === 'y') {
                return animationConfig.level !== DynamicLevel.object;
            }
            return true;
        });

        // Because of how the container hierarchy was arranged, the "shouldOffsetPropertyValue" only had an effect on
        // x values for object level animations. However, for the canvas renderer it works for all levels, so we set it to false
        // for all non-object level animations to maintain previous behaviour.
        normalStyle.animationConfigurations.forEach(animationConfig => {
            if (animationConfig.level !== DynamicLevel.object && animationConfig.property === 'x') {
                animationConfig.shouldOffsetPropertyValue = false;
            }
        });
        highlightStyle.animationConfigurations.forEach(animationConfig => {
            if (animationConfig.level === DynamicLevel.object && animationConfig.property === 'x') {
                animationConfig.shouldOffsetPropertyValue = false;
            }
        });

        // When using object & word level alpha animation on the highlight style, there used to be no effect.
        // So we filter those out to maintain previous behaviour.
        highlightStyle.animationConfigurations = highlightStyle.animationConfigurations.filter(animationConfig => {
            if (animationConfig.level === DynamicLevel.object || animationConfig.level === DynamicLevel.word) {
                return animationConfig.property !== 'alpha';
            }
            return true;
        });

        // SCALE
        // We need to remap scale.x and scale.y to scaleX and scaleY for the canvas renderer
        normalStyle.animationConfigurations.forEach(animationConfig => {
            if (animationConfig.property === 'scale.x') {
                animationConfig.property = 'scaleX';
            }
            if (animationConfig.property === 'scale.y') {
                animationConfig.property = 'scaleY';
            }
        });
        highlightStyle.animationConfigurations.forEach(animationConfig => {
            if (animationConfig.property === 'scale.x') {
                animationConfig.property = 'scaleX';
            }
            if (animationConfig.property === 'scale.y') {
                animationConfig.property = 'scaleY';
            }
        });

        // process graphics
        normalStyle.graphics.forEach(this.#manipulateGraphicSettings);
        highlightStyle.graphics.forEach(this.#manipulateGraphicSettings);
    }

    #manipulateGraphicSettings(settings: CanvasTextDynamicGraphicRendererSettings) {
        if (settings.type === 'box') {
            // Our old box graphics had two sets of properties that did the same thing. Scale & Progress animations
            // had the same effect, but the canvas renderer only supports Progress. So we need to remap scale.x
            // to xProgress (and the same for y).
            settings.animationConfigurations.forEach(animationConfig => {
                if (animationConfig.property === 'scale.x') {
                    animationConfig.property = 'xProgress';
                }
                if (animationConfig.property === 'scale.y') {
                    animationConfig.property = 'yProgress';
                }
            });
        }
    }

    /**
     * Returns an object that includes the character index of the closest character to
     * the location passed in and the distance to that index
     */
    getClosestTextIndex(coordinate: Coordinate): number {
        if (
            !coordinate ||
            typeof coordinate.x !== 'number' ||
            typeof coordinate.y !== 'number' ||
            coordinate.x < 0 ||
            coordinate.y < 0
        ) {
            return 0;
        }

        // if there's no text, put the cursor at the start
        if (this.text.length === 0) {
            return 0;
        }

        // This factor allows the click to give preference to the current line, by virtually
        // separating lines from each other. This is obvious when an adjacent line is longer
        // than the current line. If a click happens behind the shorter line then the closest
        // char will be in the adjacent line, but it's more natural to put the cursor at the end
        // of the clicked line.
        const yScale = 10;
        coordinate.y *= yScale;

        let minDistance = Infinity;
        let indexToMinDistance = 0;
        const textAssetPosition = this.getGlobalPosition();

        // note that this loop goes from 0 to _after_ the last
        // character - we are checking each letter, before and after
        // so be careful not to trigger an index exception in here
        for (let i = 0; i <= this.text.length; i++) {
            // get the line, word and character hierarchy index for the current character
            const offset = this.renderer.metrics.getOffsetsForChar(i);

            // Make sure the PIXI objects actually exist
            if (offset.lineIndex >= this._wordElements.length) {
                continue;
            }
            if (offset.wordIndex >= this._wordElements[offset.lineIndex].length) {
                continue;
            }

            // get the position (Point) of the word the character belongs to
            const textLine = this.renderer.metrics.lines[offset.lineIndex];
            const wordElement = this._wordElements[offset.lineIndex][offset.wordIndex][offset.elementIndex];

            // get the position (Point) of the character relative to the word
            const relCharacterPosition = this.renderer.metrics.positionOfCharRelativeToWordElement(
                offset.lineIndex, offset.wordIndex, offset.elementIndex, offset.characterIndex);

            // add the character offset to the position of the word to get the global character position
            const lineHeight = this.renderer.metrics.lines[offset.lineIndex].height / this.referenceToOutputMultiplier;
            const characterPosition = new PIXI.Point(
                textAssetPosition.x + (wordElement.bounds.x / this.referenceToOutputMultiplier) + (relCharacterPosition.x / this.referenceToOutputMultiplier),
                (textAssetPosition.y + textLine.bounds.y / this.referenceToOutputMultiplier + lineHeight / 2) * yScale
            );

            // collect the index of the character closest to the pixel location
            const charDistance = distance(characterPosition, coordinate);
            if (charDistance < minDistance) {
                minDistance = charDistance;
                indexToMinDistance = i;
            }
        }

        return indexToMinDistance;
    }

    override _build(): void {
        super._build();

        // Animations for CanvasTextAsset are still handled by the animation manager
        this.animationManager.reset();
        const configs = this.renderer.buildAnimationManagerConfig(this.container.duration);
        configs.map(config => this.animationManager.add(config));
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (progress >= 0 && progress <= 1) {
            this.animationManager.percentage = progress;
        }

        super._seek(progress, seekMode);
    }

    _syncFontSizeAndLeadingToStyles() {
        const scaledFontSize = this.textSize * this.textScaleFactor;
        this.normalStyle.fontSize = scaledFontSize;
        this.highlightStyle.fontSize = scaledFontSize * this.highlightedTextScale;

        // set leading based on the scaled textSize
        const leading = scaledFontSize * this.leadingMultiplier;
        this.normalStyle.leading = leading;
        this.highlightStyle.leading = leading;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            ...getAttributesToSerialize(this),
            'normalStyle',
            'highlightStyle',
            'textHighlights',
        ]);
    }
}


export const sanitizeHighlights = (text: string, highlights: [number, number][]) => {
    // discard any highlights with an invalid start index
    const filteredHighlights = highlights.filter((highlight) => {
        return highlight[0] < highlight[1]
                && highlight[0] >= 0
                && highlight[0] < text.length;
    });

    // for any highlight that exists past the text's length, cap it at the length
    const cappedHighlights: [number, number][] = filteredHighlights.map(highlight => {
        if (highlight[1] > text.length) {
            return [highlight[0], text.length];
        }
        return highlight;
    });

    // sort by start index ascending
    const sortedHighlights = cappedHighlights.sort((a, b) => a[0] - b[0]);

    // merge any overlapping highlights
    for (let i = 1; i < sortedHighlights.length; i++) {
        const firstHighlightIdx = i - 1;
        const firstHighlight = sortedHighlights[firstHighlightIdx];
        const secondHighlight = sortedHighlights[i];

        const hasOverlap = secondHighlight[0] <= firstHighlight[1]
                && secondHighlight[1] >= firstHighlight[0];
        if (!hasOverlap) {
            continue;
        }

        // replace the two compared highlights with a merged version
        const allIndices = [...firstHighlight, ...secondHighlight];
        const mergedHighlight: [number, number] = [Math.min(...allIndices), Math.max(...allIndices)];
        const spliceArgs: [number, number, [number, number]?] = [firstHighlightIdx, 2];
        if (mergedHighlight[1] - mergedHighlight[0] > 0) {
            spliceArgs.push(mergedHighlight); // the highlight must span at least one char
            i--;
        }
        sortedHighlights.splice(...spliceArgs);
    }

    return sortedHighlights;
};
